import {
  FeatureToggleCapabilityCoordinator,
  PaneVisibilityCoordinator,
  ViewMode,
} from '@mobile-mapping/web3d';
import { Accessor, Component, createMemo } from 'solid-js';
import type { CapabilityRegistryContainer } from '@trimble/web3d';
import { SelectionActionsCapability } from '@trimble/web3d-capabilities';
import { Icon } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import {
  VisibilityControl,
  type VisibilityRowConfig,
} from './VisibilityControl';
import { buildRowsForViewMode } from './build-pane-visibility-rows';

/**
 * Per-pane entry point for the visibility dropdown. Bridges the pane's
 * {@link CapabilityRegistryContainer} into the presentational
 * {@link VisibilityControl}. Row set depends on the current
 * {@link ViewMode}; see {@link buildRowsForViewMode}.
 */
export const PaneToolbarVisibilityControl: Component<{
  capRegContainer: CapabilityRegistryContainer;
  viewMode: Accessor<ViewMode>;
}> = (props) => {
  const paneCap = (): PaneVisibilityCoordinator | undefined =>
    props.capRegContainer.get(PaneVisibilityCoordinator)();
  const featureToggleCoord = ():
    | FeatureToggleCapabilityCoordinator
    | undefined =>
    props.capRegContainer.get(FeatureToggleCapabilityCoordinator)();

  const rows = createMemo<VisibilityRowConfig[]>(() => {
    const cap = paneCap();
    const coord = featureToggleCoord();
    if (!cap || !coord) return [];
    return buildRowsForViewMode(props.viewMode().type, cap, coord);
  });

  /**
   * "Show all objects" only un-hides entities hidden via this pane's
   * selection actions; transparency sliders intentionally stay where
   * they are (same contract as the main-toolbar version).
   *
   * The action is only meaningful in 3D panes — {@link
   * SelectionActionsCapability.showAll} acts on `TrimbimModel`
   * instances, which only render in 3D mode. In panorama and planar
   * panes there are no model entities to reveal, so the row is hidden
   * (see `resetAll` prop forwarding below) to avoid surfacing a no-op
   * control.
   */
  const resetAll = (): void => {
    const selectionActions = props.capRegContainer.get(
      SelectionActionsCapability
    )();
    void selectionActions?.showAll();
  };

  const showResetAll = createMemo(() => props.viewMode().type === '3D');

  return (
    <VisibilityControl
      rows={rows}
      resetAll={showResetAll() ? resetAll : undefined}
      buttonSize="small"
      menuAlign="right"
      buttonTooltipKey="mobileMapping.paneVisibility.button"
      controlTestId="pane-visibility-control"
      menuTestId="pane-visibility-menu"
      renderTrigger={({ isOpen, toggle }) => (
        <section
          class={`flex rounded-sm ${
            isOpen()
              ? 'bg-[var(--modus-wc-color-primary-pale)] hover:bg-[var(--modus-wc-color-primary-pale)]'
              : 'bg-base-page hover:bg-[var(--modus-wc-color-base-100)]'
          }`}
        >
          <button
            type="button"
            class="min-h-8 flex items-center justify-center cursor-pointer rounded-sm"
            title={t('mobileMapping.paneVisibility.button')}
            onClick={toggle}
          >
            <div class="flex flex-row items-center justify-center rounded-sm pl-1 pr-0.5">
              <Icon icon="tc-icon-eye-visibility-toggle" size="icon-small" />
              <i
                class={`icon-font tc-icon-arrow-drop-down transition-all duration-300 ${
                  isOpen() ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
        </section>
      )}
    />
  );
};
